import React, {createContext, useContext, useMemo, useState} from 'react';

type RunSelectorResizingContextValue = {
  /** the column that is being resized, if any */
  columnResizingAccessor?: string;
  /** whether the run selector is being resized (either via the full-height bar or a specific column) */
  isResizing: boolean;
  /** whether the full-height run selector resizer bar is being dragged */
  isResizingFullHeight: boolean;
  setColumnResizingAccessor: (columnAccessor?: string) => void;
  setIsResizingFullHeight: (value: boolean) => void;
};

type RunSelectorResizingContextProviderProps = {
  children: React.ReactNode;
};

const RunSelectorResizingContext =
  createContext<RunSelectorResizingContextValue | null>(null);

export const RunSelectorResizingContextProvider = ({
  children,
}: RunSelectorResizingContextProviderProps) => {
  const [columnResizingAccessor, setColumnResizingAccessor] =
    useState<string>();
  const [isResizingFullHeight, setIsResizingFullHeight] = useState(false);
  const value = useMemo<RunSelectorResizingContextValue>(
    () => ({
      columnResizingAccessor,
      isResizing: isResizingFullHeight || columnResizingAccessor != null,
      isResizingFullHeight,
      setColumnResizingAccessor,
      setIsResizingFullHeight,
    }),
    [columnResizingAccessor, isResizingFullHeight]
  );
  return (
    <RunSelectorResizingContext.Provider value={value}>
      {children}
    </RunSelectorResizingContext.Provider>
  );
};

export const useRunSelectorResizingContext =
  (): RunSelectorResizingContextValue => {
    const value = useContext(RunSelectorResizingContext);
    if (value == null) {
      throw new Error(
        'useRunSelectorResizingContext must be used within a RunSelectorResizingContextProvider'
      );
    }
    return value;
  };

export const useRunSelectorResizingContextMaybe = () =>
  useContext(RunSelectorResizingContext);
